/**
 * 役職と「できること」の一覧。
 *
 * `GET /org/roles` が返すものをそのまま並べる。項目名も説明もサーバが持っているので、
 * できることが増えても画面の作り直しは要らない（docs/01 5-4）。
 *
 * 「会社の設定・拠点の一覧・役職のできることを変える」を持つ方には、役職ごとに「変える」を出す
 * （変える画面は /roles/:key・issue #124）。見せ方だけの出し分けで、変えてよいかはサーバの門番が決める。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../api/context.js';
import type { RolesResponse } from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { loadFailure, type LoadFailure } from '../api/load-failure.js';
import { DataTable } from '../components/ui/DataTable.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { can } from '../menu/visible.js';

export function RolesPage(): ReactNode {
  const api = useApi();
  const { profile } = useSession();
  const [data, setData] = useState<RolesResponse | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const canEdit = profile?.grant ? can(profile.grant, 'settings.manage') : false;

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    api
      .get<RolesResponse>('/org/roles')
      .then((loaded) => {
        if (!cancelled) setData(loaded);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(loadFailure(caught));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, reloadKey]);

  return (
    <article>
      <PageHeader
        title="役職とできること"
        description={
          canEdit
            ? '役職ごとに、できることの一覧です（○＝できる）。変えるときは、役職の名前の下の「変える」から進みます。'
            : '役職ごとに、できることの一覧です（○＝できる）。'
        }
      />
      {canEdit ? (
        <p className="admin-note">
          あなたご自身の役職と、そこに無い「できること」を含む役職は、呼び名だけ変えられます（できることは、ほかの管理者の方に変えていただきます）。
        </p>
      ) : null}

      {error ? <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} /> : null}
      {!error && !data ? <LoadingBlock /> : null}

      {data ? (
        <DataTable label="役職ごとのできること">
          <thead>
            <tr>
              <th scope="col">できること</th>
              {data.roles.map((role) => (
                <th key={role.key} scope="col">
                  {role.display_name}
                  {profile && role.key === profile.me.role ? <small>（ご自身）</small> : null}
                  {canEdit ? (
                    <div className="admin-role-edit">
                      <Link
                        className="ui-button ui-button-secondary ui-button-compact"
                        to={`/roles/${role.key}`}
                        aria-label={`「${role.display_name}」を変える`}
                      >
                        変える
                      </Link>
                    </div>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">受け持ちの範囲</th>
              {data.roles.map((role) => (
                <td key={role.key}>{role.scope === 'org' ? '全社' : '自拠点'}</td>
              ))}
            </tr>
            {data.capabilities.map((capability) => (
              <tr key={capability.key}>
                <th scope="row">
                  {capability.description}
                  {data.fixed_capabilities?.includes(capability.key) ? (
                    <small className="admin-check-note">（どの役職でも外せません）</small>
                  ) : null}
                </th>
                {data.roles.map((role) => (
                  <td key={role.key}>
                    {role.capabilities[capability.key] === true ? (
                      <span className="admin-mark-yes" aria-label="できる">
                        ○
                      </span>
                    ) : (
                      <span className="admin-mark-no" aria-label="できない">
                        —
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </DataTable>
      ) : null}
    </article>
  );
}
